// client/src/components/media/TextPost.jsx
// Text-only multi-platform posting (Facebook, Twitter, LinkedIn, Bluesky, Threads).
// Reuses the same publish endpoints as MediaPost but never sends media.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaPaperPlane, FaArrowLeft, FaSpinner, FaCalendarCheck } from "react-icons/fa";
import AppFooter from "../AppFooter";
import MentionInput from "./MentionInput";
import TimeSelect from "../TimeSelect";
import { localToUTC, getNextSlotInTenantTZ } from "../../utils/timezoneUtils";

const TEXT_PLATFORMS = [
  { key: "facebook", label: "Facebook", color: "#1877F2", maxLen: 63206 },
  { key: "twitter",  label: "Twitter",  color: "#1DA1F2", maxLen: 280 },
  { key: "linkedin", label: "LinkedIn", color: "#0A66C2", maxLen: 3000 },
  { key: "bluesky",  label: "Bluesky",  color: "#0085FF", maxLen: 300 },
  { key: "threads",  label: "Threads",  color: "#000000", maxLen: 500 },
];

const labelOf = (key) =>
  TEXT_PLATFORMS.find((p) => p.key === key)?.label ||
  key.charAt(0).toUpperCase() + key.slice(1);

const errString = (e) => {
  const d = e?.response?.data;
  return (
    d?.error ||
    d?.message ||
    (typeof d === "string" ? d : null) ||
    e?.message ||
    "Request failed"
  );
};

export default function TextPost() {
  const { tenant } = useParams();
  const navigate = useNavigate();

  const baseUrl =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");

  const [text, setText] = useState("");
  const [platforms, setPlatforms] = useState({
    facebook: false,
    twitter:  false,
    linkedin: false,
    bluesky:  false,
    threads:  false,
  });
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Scheduling state — mirrors SchedulePost.jsx's pattern so the existing
  // /api/schedule/schedule worker can pick up text-only posts unchanged.
  const [mode, setMode] = useState("now"); // 'now' | 'schedule'
  const [tenantTimezone, setTenantTimezone] = useState("America/Chicago");
  const initialSlot = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 15);
    const min = Math.ceil(d.getMinutes() / 5) * 5;
    if (min === 60) { d.setHours(d.getHours() + 1); d.setMinutes(0); }
    else { d.setMinutes(min); }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
  }, []);
  const [scheduleDate, setScheduleDate] = useState(initialSlot.date);
  const [scheduleTime, setScheduleTime] = useState(initialSlot.time);

  useEffect(() => {
    if (!tenant) return;
    (async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/api/tenants/${tenant}/timezone`,
          { withCredentials: true }
        );
        if (res.data?.timezone) {
          setTenantTimezone(res.data.timezone);
          const nextSlot = getNextSlotInTenantTZ(res.data.timezone, 15);
          setScheduleDate(nextSlot.date);
          setScheduleTime(nextSlot.time);
        }
      } catch (e) {
        // fall back to browser tz
        const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTZ) setTenantTimezone(browserTZ);
      }
    })();
  }, [tenant, baseUrl]);

  const selected = useMemo(
    () => Object.entries(platforms).filter(([, v]) => v).map(([k]) => k),
    [platforms]
  );

  const togglePlatform = (key) =>
    setPlatforms((prev) => ({ ...prev, [key]: !prev[key] }));

  // Strictest length limit across selected platforms
  const lowestMaxLen = useMemo(() => {
    const lens = TEXT_PLATFORMS
      .filter((p) => platforms[p.key])
      .map((p) => p.maxLen);
    return lens.length ? Math.min(...lens) : Infinity;
  }, [platforms]);

  const overLimit = text.length > lowestMaxLen;
  const canSubmit =
    !busy && text.trim().length > 0 && selected.length > 0 && !overLimit;

  const scheduleSubmit = async () => {
    setBusy(true);
    setError("");
    setSuccessMessage("");
    setResults(null);

    const tenantId = Number(tenant) || 1;
    const trimmed = text.trim();

    try {
      // Pre-flight: verify selected platforms are connected
      try {
        const statusRes = await fetch(
          `${baseUrl}/api/integrations/status/${tenantId}`,
          { headers: { "Content-Type": "application/json" }, credentials: "include" }
        );
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          if (statusData?.success) {
            const statuses = statusData.integrations || {};
            const unauthorized = selected.filter((p) => !statuses[p]?.connected);
            if (unauthorized.length) {
              const names = unauthorized.map(labelOf).join(", ");
              setError(`Please connect ${names} before scheduling.`);
              setBusy(false);
              return;
            }
          }
        }
      } catch (e) {
        console.warn("[TextPost] could not verify platform authorization:", e?.message);
      }

      // Convert tenant-local datetime to a UTC MySQL string the scheduler accepts
      const scheduledUtcMySqlRaw = localToUTC(scheduleDate, scheduleTime, tenantTimezone);
      const scheduledUtcMySql =
        /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(scheduledUtcMySqlRaw)
          ? `${scheduledUtcMySqlRaw}:00`
          : scheduledUtcMySqlRaw;
      const scheduledUtcIso = new Date(scheduledUtcMySql.replace(" ", "T") + "Z").toISOString();

      if (new Date(scheduledUtcIso) <= new Date()) {
        setError("Scheduled time must be in the future. Please pick a later date/time.");
        setBusy(false);
        return;
      }

      const { data } = await axios.post(
        `/api/schedule/schedule`,
        {
          tenantId,
          platforms: selected,
          content: trimmed,
          caption: trimmed,
          mediaUrls: [],
          scheduledAtLocal: scheduledUtcMySql,
          scheduledAtIso: scheduledUtcIso,
          scheduledAt: scheduledUtcIso,
          timezone: tenantTimezone,
        },
        { withCredentials: true, timeout: 30000 }
      );

      if (!data?.success) {
        throw new Error(data?.error || "Failed to schedule post");
      }

      const display = new Date(scheduledUtcIso).toLocaleString("en-US", {
        timeZone: tenantTimezone,
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
      });
      setSuccessMessage(
        `Post scheduled for ${display} on ${selected.map(labelOf).join(", ")}.`
      );
      setText("");
    } catch (e) {
      setError(errString(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (mode === "schedule") return scheduleSubmit();

    setBusy(true);
    setError("");
    setSuccessMessage("");
    setResults(null);

    const tenantId = Number(tenant) || 1;
    const trimmed = text.trim();

    // Pre-flight: verify selected platforms are connected (mirrors MediaPost)
    try {
      const statusRes = await fetch(
        `${baseUrl}/api/integrations/status/${tenantId}`,
        { headers: { "Content-Type": "application/json" }, credentials: "include" }
      );
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (statusData?.success) {
          const statuses = statusData.integrations || {};
          const unauthorized = selected.filter((p) => !statuses[p]?.connected);
          if (unauthorized.length) {
            const names = unauthorized.map(labelOf).join(", ");
            setError(`Please connect ${names} before posting.`);
            setBusy(false);
            return;
          }
        }
      }
    } catch (e) {
      // non-fatal — let the server reject if necessary
      console.warn("[TextPost] Could not verify platform authorization:", e?.message);
    }

    const out = {};

    // 1) Facebook + Threads via the generic hub (both accept text-only)
    const hubPlatforms = selected.filter(
      (p) => p === "facebook" || p === "threads"
    );
    if (hubPlatforms.length) {
      try {
        const { data } = await axios.post(
          `${baseUrl}/api/integrations/publish`,
          {
            tenantId,
            platforms: hubPlatforms,
            content: trimmed,
            caption: trimmed,
            mediaUrls: [],
          },
          { withCredentials: true, timeout: 120000 }
        );
        const perPlatform = data?.results || {};
        for (const p of hubPlatforms) {
          const r = perPlatform[p];
          if (r?.ok) {
            out[p] = { ok: true };
          } else {
            const errVal = r?.error;
            out[p] = {
              ok: false,
              error:
                typeof errVal === "string"
                  ? errVal
                  : errVal?.error || errVal?.message || JSON.stringify(errVal || "failed"),
            };
          }
        }
      } catch (e) {
        for (const p of hubPlatforms) {
          out[p] = { ok: false, error: errString(e) };
        }
      }
    }

    // 2) Twitter (direct)
    if (platforms.twitter) {
      try {
        await axios.post(
          `${baseUrl}/api/integrations/twitter/publish`,
          { tenantId, text: trimmed, imageUrls: [], videoUrls: [] },
          { withCredentials: true, timeout: 120000 }
        );
        out.twitter = { ok: true };
      } catch (e) {
        out.twitter = { ok: false, error: errString(e) };
      }
    }

    // 3) LinkedIn (direct, text-only path)
    if (platforms.linkedin) {
      try {
        await axios.post(
          `${baseUrl}/api/integrations/linkedin/publish`,
          { tenantId, text: trimmed, as: "member" },
          { withCredentials: true, timeout: 60000 }
        );
        out.linkedin = { ok: true };
      } catch (e) {
        out.linkedin = { ok: false, error: errString(e) };
      }
    }

    // 4) Bluesky (direct)
    if (platforms.bluesky) {
      try {
        await axios.post(
          `${baseUrl}/api/integrations/bluesky/publish`,
          { tenantId, text: trimmed, mediaUrls: [] },
          { withCredentials: true, timeout: 60000 }
        );
        out.bluesky = { ok: true };
      } catch (e) {
        out.bluesky = { ok: false, error: errString(e) };
      }
    }

    setResults(out);
    setBusy(false);

    // If every selected platform succeeded, clear the editor so the user
    // knows the post went through. Otherwise leave the text intact for retry.
    const allOk = selected.every((p) => out[p]?.ok);
    if (allOk) setText("");
  };

  const counterColor = overLimit ? "#dc3545" : "inherit";

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">New Text Post</h2>

      <div className="form-group">
        <label className="form-label">Post Text</label>
        <MentionInput
          value={text}
          onChange={setText}
          rows={6}
          placeholder="What do you want to share? Type @ to mention a platform handle."
        />
        <div
          className="form-text"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <span>Type @ to mention platform handles (e.g. @nike).</span>
          <span style={{ color: counterColor }}>
            {text.length}
            {lowestMaxLen !== Infinity ? ` / ${lowestMaxLen}` : ""}
          </span>
        </div>
      </div>

      <div className="form-group" style={{ marginTop: "1rem" }}>
        <div className="form-label font-semibold" style={{ marginBottom: 8 }}>
          Select Platforms
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {TEXT_PLATFORMS.map((p) => {
            const isSelected = !!platforms[p.key];
            const r = results?.[p.key];
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => togglePlatform(p.key)}
                disabled={busy}
                className="btn"
                style={{
                  borderRadius: 20,
                  padding: "8px 14px",
                  fontSize: 14,
                  fontWeight: 500,
                  border: `1px solid ${isSelected ? p.color : "#dee2e6"}`,
                  background: isSelected ? p.color : "transparent",
                  color: isSelected ? "#fff" : "inherit",
                  cursor: busy ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
                title={`Max ${p.maxLen} characters`}
              >
                {p.label}
                {r?.ok && <span aria-label="posted">✓</span>}
                {r && !r.ok && <span aria-label="failed">✗</span>}
              </button>
            );
          })}
        </div>
        {selected.length === 0 && (
          <div className="text-gray-400 small" style={{ marginTop: 8 }}>
            Select one or more platforms.
          </div>
        )}
        {overLimit && (
          <div className="text-danger small" style={{ marginTop: 8 }}>
            Text exceeds the {lowestMaxLen}-character limit of the strictest
            selected platform.
          </div>
        )}
      </div>

      {/* Post mode toggle */}
      <div className="form-group" style={{ marginTop: "1rem" }}>
        <div className="form-label font-semibold" style={{ marginBottom: 8 }}>
          When to Post
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setMode("now")}
            className="btn"
            style={{
              borderRadius: 20, padding: "8px 14px", fontSize: 14, fontWeight: 500,
              border: `1px solid ${mode === "now" ? "#ff6800" : "#dee2e6"}`,
              background: mode === "now" ? "#ff6800" : "transparent",
              color: mode === "now" ? "#fff" : "inherit",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <FaPaperPlane /> Post Now
          </button>
          <button
            type="button"
            onClick={() => setMode("schedule")}
            className="btn"
            style={{
              borderRadius: 20, padding: "8px 14px", fontSize: 14, fontWeight: 500,
              border: `1px solid ${mode === "schedule" ? "#ff6800" : "#dee2e6"}`,
              background: mode === "schedule" ? "#ff6800" : "transparent",
              color: mode === "schedule" ? "#fff" : "inherit",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <FaCalendarCheck /> Schedule
          </button>
        </div>
      </div>

      {mode === "schedule" && (
        <div className="mb-3 p-3 rounded" style={{ background: "#f8f9fa", color: "#212529", marginTop: "1rem" }}>
          <div className="form-label" style={{ color: "#212529" }}>
            <strong>Schedule Date & Time</strong>
          </div>
          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="form-label small mb-1" htmlFor="text-schedule-date" style={{ color: "#6c757d" }}>Date</label>
              <input
                id="text-schedule-date"
                type="date"
                className="form-control"
                value={scheduleDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setScheduleDate(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small mb-1" htmlFor="text-schedule-time" style={{ color: "#6c757d" }}>Time</label>
              <TimeSelect
                id="text-schedule-time"
                value={scheduleTime}
                onChange={setScheduleTime}
                tenantTimezone={tenantTimezone}
                showTimezone={true}
              />
            </div>
          </div>
        </div>
      )}

      {!!error && (
        <div className="alert alert-danger" style={{ marginTop: "1rem" }}>
          {error}
        </div>
      )}

      {!!successMessage && (
        <div className="alert alert-success" style={{ marginTop: "1rem" }}>
          {successMessage}
        </div>
      )}

      {results && (
        <div className="form-group" style={{ marginTop: "1rem" }}>
          <div className="form-label font-semibold">Results</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {Object.entries(results).map(([k, r]) => (
              <li key={k} style={{ marginBottom: 4 }}>
                <strong>{labelOf(k)}:</strong>{" "}
                {r.ok ? (
                  <span style={{ color: "#198754" }}>Posted</span>
                ) : (
                  <span style={{ color: "#dc3545" }}>{r.error}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Desktop inline action bar (matches NewPost / MediaPost) */}
      <div className="desktop-action-bar">
        <button
          type="button"
          onClick={() => navigate(`/${tenant}/post`)}
          disabled={busy}
        >
          <FaArrowLeft /> Back
        </button>
        <button
          type="button"
          className="btn-primary-action"
          onClick={submit}
          disabled={!canSubmit}
        >
          {(() => {
            if (busy) {
              return (<><FaSpinner /> {mode === "schedule" ? "Scheduling…" : "Posting…"}</>);
            }
            if (mode === "schedule") {
              return (<><FaCalendarCheck /> Schedule Post</>);
            }
            return (<><FaPaperPlane /> Post</>);
          })()}
        </button>
      </div>

      <AppFooter
        buttons={[
          {
            id: "back",
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(`/${tenant}/post`),
            disabled: busy,
          },
          {
            id: "post",
            icon: busy
              ? <FaSpinner />
              : (mode === "schedule" ? <FaCalendarCheck /> : <FaPaperPlane />),
            label: busy
              ? (mode === "schedule" ? "Scheduling…" : "Posting…")
              : (mode === "schedule" ? "Schedule Post" : "Post"),
            onClick: submit,
            disabled: !canSubmit,
          },
        ]}
      />
    </div>
  );
}
